/**
 * External dependencies
 */

/**
 * WordPress dependencies
 */
import { Button, privateApis as componentsPrivateApis, __experimentalHStack as HStack } from '@wordpress/components';
import { __, _x } from '@wordpress/i18n';
import { memo } from '@wordpress/element';
import { cog } from '@wordpress/icons';

/**
 * Internal dependencies
 */
import { unlock } from './lock-unlock';
import { SORTING_DIRECTIONS, sortLabels } from './constants';
import { VIEW_LAYOUTS } from './layouts';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const {
  DropdownMenuV2: DropdownMenu,
  DropdownMenuGroupV2: DropdownMenuGroup,
  DropdownMenuItemV2: DropdownMenuItem,
  DropdownMenuRadioItemV2: DropdownMenuRadioItem,
  DropdownMenuCheckboxItemV2: DropdownMenuCheckboxItem,
  DropdownMenuItemLabelV2: DropdownMenuItemLabel
} = unlock(componentsPrivateApis);
function ViewTypeMenu({
  view,
  onChangeView,
  defaultLayouts = {
    list: {},
    grid: {},
    table: {}
  }
}) {
  const availableLayouts = Object.keys(defaultLayouts);
  if (availableLayouts.length <= 1) {
    return null;
  }
  return availableLayouts.map(layout => {
    const config = VIEW_LAYOUTS.find(v => v.type === layout);
    if (!config) {
      return null;
    }
    return /*#__PURE__*/_jsx(DropdownMenuRadioItem, {
      value: layout,
      name: "view-actions-available-view",
      checked: layout === view.type,
      hideOnClick: true,
      onChange: e => {
        switch (e.target.value) {
          case 'list':
          case 'grid':
          case 'table':
            return onChangeView({
              ...view,
              type: e.target.value,
              ...defaultLayouts[e.target.value]
            });
        }
        throw new Error('Invalid dataview');
      },
      children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
        children: config.label
      })
    }, layout);
  });
}
const PAGE_SIZE_VALUES = [10, 20, 50, 100];
function PageSizeMenu({
  view,
  onChangeView
}) {
  return /*#__PURE__*/_jsx(DropdownMenu, {
    trigger: /*#__PURE__*/_jsx(DropdownMenuItem, {
      suffix: /*#__PURE__*/_jsx("span", {
        "aria-hidden": "true",
        children: view.perPage
      }),
      children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
        children: __('Items per page')
      })
    }),
    children: PAGE_SIZE_VALUES.map(size => {
      return /*#__PURE__*/_jsx(DropdownMenuRadioItem, {
        value: size,
        name: "view-actions-page-size",
        checked: view.perPage === size,
        onChange: () => {
          onChangeView({
            ...view,
            // `e.target.value` holds the same value as `size` but as a string,
            // so we use `size` directly to avoid parsing to int.
            perPage: size,
            page: 1
          });
        },
        children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
          children: size
        })
      }, size);
    })
  });
}
function FieldsVisibilityMenu({
  view,
  onChangeView,
  fields
}) {
  const hidableFields = fields.filter(field => field.enableHiding !== false && field.id !== view?.layout?.mediaField);
  const viewFields = view.fields || fields.map(field => field.id);
  if (!hidableFields?.length) {
    return null;
  }
  return /*#__PURE__*/_jsx(DropdownMenu, {
    trigger: /*#__PURE__*/_jsx(DropdownMenuItem, {
      children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
        children: __('Fields')
      })
    }),
    children: hidableFields?.map(field => {
      return /*#__PURE__*/_jsx(DropdownMenuCheckboxItem, {
        value: field.id,
        checked: viewFields.includes(field.id),
        onChange: () => {
          onChangeView({
            ...view,
            fields: viewFields.includes(field.id) ? viewFields.filter(id => id !== field.id) : [...viewFields, field.id]
          });
        },
        children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
          children: field.header
        })
      }, field.id);
    })
  });
}
function SortMenu({
  fields,
  view,
  onChangeView
}) {
  const sortableFields = fields.filter(field => field.enableSorting !== false);
  if (!sortableFields?.length) {
    return null;
  }
  const currentSortedField = fields.find(field => field.id === view.sort?.field);
  return /*#__PURE__*/_jsx(DropdownMenu, {
    trigger: /*#__PURE__*/_jsx(DropdownMenuItem, {
      suffix: /*#__PURE__*/_jsx("span", {
        "aria-hidden": "true",
        children: currentSortedField?.header
      }),
      children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
        children: __('Sort by')
      })
    }),
    children: sortableFields?.map(field => {
      const sortedDirection = view.sort?.direction;
      return /*#__PURE__*/_jsx(DropdownMenu, {
        trigger: /*#__PURE__*/_jsx(DropdownMenuItem, {
          children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
            children: field.header
          })
        }),
        style: {
          minWidth: '220px'
        },
        children: SORTING_DIRECTIONS.map(direction => {
          const isChecked = currentSortedField !== undefined && sortedDirection === direction && field.id === currentSortedField.id;
          const value = `${field.id}-${direction}`;
          return /*#__PURE__*/_jsx(DropdownMenuRadioItem, {
            // All sorting radio items share the same name, so that
            // selecting a sorting option automatically deselects the
            // previously selected one, even if it is displayed in
            // another submenu. The field and direction are passed via
            // the `value` prop.
            name: "view-actions-sorting",
            value: value,
            checked: isChecked,
            onChange: () => {
              onChangeView({
                ...view,
                sort: {
                  field: field.id,
                  direction
                }
              });
            },
            children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
              children: sortLabels[direction]
            })
          }, value);
        })
      }, field.id);
    })
  });
}
function _ViewActions({
  fields,
  view,
  onChangeView,
  defaultLayouts
}) {
  const activeView = VIEW_LAYOUTS.find(v => view.type === v.type);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(HStack, {
      spacing: 1,
      expanded: false,
      style: {
        flexShrink: 0
      },
      children: [/*#__PURE__*/_jsx(DropdownMenu, {
        trigger: /*#__PURE__*/_jsx(Button, {
          size: "compact",
          icon: activeView?.icon,
          label: __('Layout')
        }),
        children: /*#__PURE__*/_jsx(ViewTypeMenu, {
          view: view,
          onChangeView: onChangeView,
          defaultLayouts: defaultLayouts
        })
      }), /*#__PURE__*/_jsx(DropdownMenu, {
        trigger: /*#__PURE__*/_jsx(Button, {
          size: "compact",
          icon: cog,
          label: _x('View options', 'View is used as a noun')
        }),
        children: /*#__PURE__*/_jsxs(DropdownMenuGroup, {
          children: [/*#__PURE__*/_jsx(SortMenu, {
            fields: fields,
            view: view,
            onChangeView: onChangeView
          }), /*#__PURE__*/_jsx(FieldsVisibilityMenu, {
            fields: fields,
            view: view,
            onChangeView: onChangeView
          }), /*#__PURE__*/_jsx(PageSizeMenu, {
            view: view,
            onChangeView: onChangeView
          })]
        })
      })]
    })
  });
}

// A type assertion is used here to keep the type argument.
const ViewActions = memo(_ViewActions);
export default ViewActions;
//# sourceMappingURL=view-actions.js.map